import * as S from './drag-drop.styles';

import React from 'react';
import {useTyping} from '../TypingContext';
import {Transforms, Path} from 'slate';
import {
  RenderElementProps,
  useReadOnly,
  useEditor,
  ReactEditor,
} from 'slate-react';

import makeComp from '../../../util/profiler';
import {WBSlateElement} from '../WBSlate';
import {EditorWithImages} from './images';

import {Node} from 'slate';

export type DragType = 'block' | 'text';

export const getDragType = (items: DataTransferItemList) => {
  // tslint:disable-next-line:prefer-for-of
  for (let i = 0; i < items.length; i++) {
    const {kind, type} = items[i];
    if (type === 'application/x-slate-fragment' || kind === 'file') {
      return 'block';
    }
  }

  return 'text';
};

interface WBSlateDragDropContextValue {
  dragItem?: Node;
  setDragItem(node: Node | undefined): void;
}

export const WBSlateDragDropContext = React.createContext<
  WBSlateDragDropContextValue
>({} as any);

export const useDrag = (item: Node, options?: {onHandleMouseDown?(): void}) => {
  const {setDragItem} = React.useContext(WBSlateDragDropContext);
  const [dragPhase, setDragPhase] = React.useState<
    'none' | 'renderingGhost' | 'dragging'
  >('none');
  const [grabbedHandle, setGrabbedHandle] = React.useState(false);

  const sourceAttributes = React.useMemo(
    () => ({
      draggable: grabbedHandle,
      onDragStart() {
        if (grabbedHandle) {
          setDragItem(item);
          setDragPhase('renderingGhost');
          setGrabbedHandle(false);
        }
      },
      onDragEnd() {
        setDragItem(undefined);
        setDragPhase('none');
      },
      onDrag() {
        if (dragPhase === 'renderingGhost') {
          setDragPhase('dragging');
        }
      },
    }),
    [grabbedHandle, dragPhase, item, setDragItem]
  );

  const handleAttributes = React.useMemo(
    () => ({
      onMouseDown() {
        options?.onHandleMouseDown?.();
        setGrabbedHandle(true);
      },
      onMouseUp() {
        setGrabbedHandle(false);
      },
    }),
    [options]
  );

  return {sourceAttributes, handleAttributes, dragPhase};
};

export const useDrop = (options?: {
  onDragEnter?(e: React.DragEvent): void;
  onDragLeave?(e: React.DragEvent): void;
  onDrop?(e: React.DragEvent): void;
}) => {
  const {setDragItem} = React.useContext(WBSlateDragDropContext);
  const [dragType, setDragType] = React.useState<DragType | undefined>(
    undefined
  );
  const counter = React.useRef<number>(0);

  const onDragEnter = React.useCallback(
    (e: React.DragEvent) => {
      if (counter.current === 0) {
        options?.onDragEnter?.(e);

        setDragType(getDragType(e.dataTransfer.items));
      }

      counter.current++;
    },
    [options]
  );

  const onDragLeave = React.useCallback(
    (e: React.DragEvent) => {
      counter.current--;

      if (counter.current === 0) {
        options?.onDragLeave?.(e);

        setDragType(undefined);
      }
    },
    [options]
  );

  const onDrop = React.useCallback(
    (e: React.DragEvent) => {
      options?.onDrop?.(e);

      counter.current = 0;
      setDragType(undefined);
      setDragItem(undefined);
    },
    [options, setDragItem]
  );

  const targetAttributes = React.useMemo(
    () => ({
      onDragEnter,
      onDragLeave,
      onDrop,
    }),
    [onDragEnter, onDragLeave, onDrop]
  );

  return {targetAttributes, dragType};
};

export const BlockWrapper: React.FC<RenderElementProps & {
  disableClickSelect?: boolean;
  noDragging?: boolean;
}> = ({attributes, element, children, disableClickSelect, noDragging}) => {
  const editor = useEditor();
  const readOnly = useReadOnly();
  const {dragItem} = React.useContext(WBSlateDragDropContext);

  const {sourceAttributes, handleAttributes, dragPhase} = useDrag(element, {
    onHandleMouseDown() {
      Transforms.select(editor, ReactEditor.findPath(editor, element));
    },
  });

  const {targetAttributes, dragType} = useDrop({
    onDragEnter(e) {
      e.preventDefault();
    },
    onDrop(e) {
      const data = e.dataTransfer;
      const fragment = data.getData('application/x-slate-fragment');

      if (fragment) {
        const decoded = decodeURIComponent(window.atob(fragment));
        const parsed = JSON.parse(decoded) as WBSlateElement[];

        const destinationPath = ReactEditor.findPath(editor, element);

        let sourcePath: Path | null = null;

        if (dragItem != null) {
          sourcePath = ReactEditor.findPath(editor, dragItem);
        }

        if (sourcePath == null) {
          // Assume external source; simply insert.
          destinationPath[destinationPath.length - 1]++;
          Transforms.insertNodes(editor, parsed, {
            at: destinationPath,
          });
          Transforms.select(editor, destinationPath);
        } else {
          if (
            destinationPath.length !== sourcePath.length ||
            Path.isBefore(destinationPath, sourcePath)
          ) {
            destinationPath[destinationPath.length - 1]++;
          }

          Transforms.moveNodes(editor, {
            at: sourcePath,
            to: destinationPath,
          });
        }

        e.stopPropagation();
        e.preventDefault();
        return;
      }

      // For dropping images directly into report; doesn't really belong in this file
      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < data.files.length; i++) {
        const file = data.files[i];
        if (file.type.includes('image')) {
          const destinationPath = ReactEditor.findPath(editor, element);
          destinationPath[destinationPath.length - 1]++;
          EditorWithImages.insertImageFromData(editor, file, {
            at: destinationPath,
          });
          e.stopPropagation();
          e.preventDefault();
          return;
        }
      }
    },
  });

  if (readOnly) {
    return children;
  }

  return (
    <S.FullBlockWrapper
      {...attributes}
      {...targetAttributes}
      {...sourceAttributes}
      onDragOver={e => {
        if (dragType === 'block') {
          e.preventDefault();
        }
      }}
      onMouseDown={e => {
        if (e.target === attributes.ref.current) {
          e.preventDefault();
          ReactEditor.focus(editor);
          Transforms.deselect(editor);
        }

        if (disableClickSelect) {
          if (
            (e.target as HTMLElement).parentElement === attributes.ref.current
          ) {
            e.preventDefault();
            ReactEditor.focus(editor);
            Transforms.deselect(editor);
          }
        }
      }}
      onClick={e => {
        if (disableClickSelect) {
          e.stopPropagation();
        }
      }}>
      <S.BlockWrapper
        dragging={dragPhase === 'dragging'}
        isDropTarget={dragType === 'block'}
        disableClickSelect={disableClickSelect}>
        {!readOnly && !noDragging && (
          <BlockDragHandle handleAttributes={handleAttributes} />
        )}
        <div>{children}</div>
      </S.BlockWrapper>
    </S.FullBlockWrapper>
  );
};

interface BlockDragHandleProps {
  handleAttributes: any;
}

const BlockDragHandle: React.FC<BlockDragHandleProps> = makeComp(
  ({handleAttributes}) => {
    const {typing} = useTyping();
    return (
      <S.DragHandle
        contentEditable={false}
        hidden={typing}
        {...handleAttributes}
      />
    );
  },
  {id: 'BlockDragHandle', memo: true}
);
